import React, { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { ConfirmDialog, DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import dayjs from "utils/dayjs";
import ApiKeyStatus from "enums/ApiKeyStatus";
import { ApiKey } from "hooks/useApiKeys";
import { Stack } from "components/layout/reusableLayouts";
import { apiKeyRoleLabelKey } from "utils/apiKeys/apiKeyStatus";
import RenderApiKeyStatus from "./RenderApiKeyStatus";

interface ApiKeyTableProps {
  apiKeys: ApiKey[];
  loading: boolean;
  // A failed list query is not an empty account, and the empty state says it is.
  loadError?: boolean;
  // Map of apiKeyId -> total calls over the metrics window (used for the
  // "30-day call count" column). Parent passes metrics rollups in; missing
  // entries render as "-".
  callTotals?: Record<string, number>;
  // True once the permission catalogue has loaded.
  showV2?: boolean;
  onRevoke: (id: string) => Promise<void> | void;
  onRotate: (id: string) => Promise<void> | void;
  onEdit?: (apiKey: ApiKey) => void;
  onViewMetrics?: (id: string) => void;
}

const ApiKeyTable: React.FC<ApiKeyTableProps> = ({
  apiKeys,
  loading,
  loadError,
  callTotals,
  showV2 = false,
  onRevoke,
  onRotate,
  onEdit,
  onViewMetrics,
}) => {
  const { t } = useTranslation();
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKey | null>(null);
  const [confirmRotate, setConfirmRotate] = useState<ApiKey | null>(null);
  // Per-action, because the `loading` prop is the list's. Without it the
  // dialog stays open across the await with a live Confirm, and a second
  // click sends a second revoke.
  const [revoking, setRevoking] = useState(false);
  const [rotating, setRotating] = useState(false);
  const columns: DataColumn<ApiKey>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("Name"),
        cell: (key) => key.name,
      },
      {
        id: "role",
        header: t("Role"),
        cell: (key) => t(apiKeyRoleLabelKey(key.role)),
      },
      {
        id: "status",
        header: t("Status"),
        cell: (key) => <RenderApiKeyStatus status={key.status} />,
      },
      ...(showV2
        ? [
            {
              id: "v2",
              header: t("ApiKeyV2Column"),
              cell: (key: ApiKey) =>
                key.v2Enrolled
                  ? t("ApiKeyV2On", { count: key.permissions?.length ?? 0 })
                  : t("ApiKeyV2Off"),
            },
          ]
        : []),
      {
        id: "lastUsed",
        header: t("Last Used"),
        cell: (key) =>
          key.lastUsedAt ? dayjs(key.lastUsedAt).fromNow() : t("Never"),
      },
      {
        id: "created",
        header: t("Created"),
        cell: (key) =>
          key.createdAt ? dayjs(key.createdAt).format("MMM D, YYYY") : "-",
      },
      {
        id: "expires",
        header: t("Expires"),
        cell: (key) =>
          key.expiresAt
            ? dayjs(key.expiresAt).format("MMM D, YYYY")
            : t("Never"),
      },
      {
        id: "calls",
        header: t("30-Day Calls"),
        cell: (key) => {
          const calls = callTotals?.[key.id];
          return typeof calls === "number" ? calls.toLocaleString() : "-";
        },
      },
    ],
    [t, callTotals, showV2],
  );

  const rowActions = (key: ApiKey): RowAction[] => {
    const inactive = key.status !== ApiKeyStatus.ACTIVE;

    return [
      ...(onViewMetrics
        ? [
            {
              label: t("View usage"),
              onClick: () => onViewMetrics(key.id),
            },
          ]
        : []),
      ...(onEdit
        ? [
            {
              label: t("Edit"),
              disabled: inactive,
              onClick: () => onEdit(key),
            },
          ]
        : []),
      {
        label: t("Rotate secret"),
        disabled: inactive,
        danger: true,
        onClick: () => setConfirmRotate(key),
      },
      {
        label: t("Deactivate"),
        disabled: inactive,
        danger: true,
        onClick: () => setConfirmRevoke(key),
      },
    ];
  };

  return (
    <>
      <DataTable<ApiKey>
        columns={columns}
        rows={apiKeys}
        getRowKey={(key) => key.id}
        loading={loading}
        errorState={
          loadError ? (
            <Typography
              variant="body1"
              sx={{ color: "var(--color-text-danger)" }}
            >
              {t("ApiKeyListLoadError")}
            </Typography>
          ) : undefined
        }
        emptyState={
          <Stack gap="var(--space-1)">
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              {t("No API keys yet")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("ApiKeyEmptyStateSubtitle")}
            </Typography>
          </Stack>
        }
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        stickyHeader
      />

      <ConfirmDialog
        open={!!confirmRevoke}
        tone="danger"
        title={t("Deactivate API key")}
        message={
          <Trans
            i18nKey="ApiKeyDeactivateConfirm"
            values={{ name: confirmRevoke?.name }}
            components={{ strong: <strong /> }}
          />
        }
        confirmLabel={t("Deactivate")}
        cancelLabel={t("No")}
        busy={revoking}
        onConfirm={async () => {
          if (!confirmRevoke || revoking) return;
          setRevoking(true);
          try {
            await onRevoke(confirmRevoke.id);
            setConfirmRevoke(null);
          } catch {
            // The caller has already reported it. Staying open is the point:
            // the action can be retried without reopening from the kebab.
          } finally {
            setRevoking(false);
          }
        }}
        onCancel={() => {
          if (revoking) return;
          setConfirmRevoke(null);
        }}
      />

      <ConfirmDialog
        open={!!confirmRotate}
        tone="danger"
        title={t("Rotate API key secret")}
        message={
          <Trans
            i18nKey="ApiKeyRotateConfirm"
            values={{ name: confirmRotate?.name }}
            components={{ strong: <strong /> }}
          />
        }
        confirmLabel={t("Rotate")}
        cancelLabel={t("No")}
        busy={rotating}
        onConfirm={async () => {
          if (!confirmRotate || rotating) return;
          setRotating(true);
          try {
            await onRotate(confirmRotate.id);
            setConfirmRotate(null);
          } catch {
            // Left open to retry, as above.
          } finally {
            setRotating(false);
          }
        }}
        onCancel={() => {
          if (rotating) return;
          setConfirmRotate(null);
        }}
      />
    </>
  );
};

export default ApiKeyTable;
